import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { JOBS } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, Plus, MapPin, BarChart3 } from "lucide-react";

const STATUSES = ["open", "active", "on_hold", "complete", "invoiced", "legacy"];

export default function JobDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [job, setJob] = useState(null);
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState(null);
    const [reason, setReason] = useState("");

    const [vOpen, setVOpen] = useState(false);
    const [vForm, setVForm] = useState({ description: "", amount: "", approved: false });
    const [cOpen, setCOpen] = useState(false);
    const [cForm, setCForm] = useState({ label: "", amount: "" });

    useEffect(() => {
        (async () => {
            const [j, cs] = await Promise.all([
                api.get(`/jobs/${id}`).then((r) => r.data),
                api.get("/clients").then((r) => r.data),
            ]);
            setJob(j);
            setClients(cs);
            setForm({
                client_id: j.client_id || "",
                address: j.address || "",
                description: j.description || "",
                status: j.status,
                quoted_amount: j.quoted_amount ?? "",
            });
        })();
    }, [id]);

    const save = async () => {
        try {
            const body = {
                client_id: form.client_id || null,
                address: form.address,
                description: form.description,
                status: form.status,
                quoted_amount: form.quoted_amount === "" ? null : parseFloat(form.quoted_amount),
                reason: reason || undefined,
            };
            const { data } = await api.patch(`/jobs/${id}`, body);
            setJob(data);
            setReason("");
            toast.success("Saved");
        } catch (e) {
            toast.error(apiErr(e));
        }
    };

    const addVariation = async () => {
        try {
            await api.post(`/jobs/${id}/variations`, {
                description: vForm.description,
                amount: parseFloat(vForm.amount) || 0,
                approved: vForm.approved,
            });
            const { data } = await api.get(`/jobs/${id}`);
            setJob(data);
            setVOpen(false);
            setVForm({ description: "", amount: "", approved: false });
            toast.success("Variation added");
        } catch (e) { toast.error(apiErr(e)); }
    };

    const addCost = async () => {
        try {
            await api.post(`/jobs/${id}/contractor-costs`, {
                label: cForm.label,
                amount: parseFloat(cForm.amount) || 0,
            });
            const { data } = await api.get(`/jobs/${id}`);
            setJob(data);
            setCOpen(false);
            setCForm({ label: "", amount: "" });
            toast.success("Contractor cost added");
        } catch (e) { toast.error(apiErr(e)); }
    };

    if (!job || !form) return <div className="text-muted">Loading…</div>;
    const totalVariations = job.variations.reduce((s, v) => s + (v.amount || 0), 0);
    const totalCosts = job.contractor_costs.reduce((s, c) => s + (c.amount || 0), 0);

    return (
        <div data-testid={JOBS.detail} className="space-y-8 max-w-5xl">
            <button
                onClick={() => nav("/admin/jobs")}
                className="text-muted hover:text-ink flex items-center gap-1.5 text-sm"
            >
                <ArrowLeft size={16} /> Jobs
            </button>

            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-5">
                    <div className="w-14 h-14 rounded-md bg-[color:var(--km-accent)]/10 flex items-center justify-center">
                        <MapPin size={22} className="text-accent" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-muted">Job</div>
                            <div className="num text-sm text-ink">{job.job_number || "unnumbered"}</div>
                            {job.legacy && <StatusBadge status="legacy" />}
                        </div>
                        <h1 className="font-extrabold tracking-[-0.03em] text-3xl leading-none mt-1">{job.address}</h1>
                        <div className="text-muted mt-2 text-sm">{job.description || "—"}</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={() => nav(`/admin/jobs/${id}/audit`)}
                        className="gap-2"
                        data-testid="job-open-audit"
                    >
                        <BarChart3 size={14} /> P&amp;L Audit
                    </Button>
                    <StatusBadge status={job.status} />
                </div>
            </div>

            <Tabs defaultValue="overview">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="variations">Variations · {job.variations.length}</TabsTrigger>
                    <TabsTrigger value="costs">Contractor costs · {job.contractor_costs.length}</TabsTrigger>
                    <TabsTrigger value="assignments" disabled>Assignments · Phase 4</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-6">
                    <section className="km-card p-6 space-y-5">
                        <h2 className="font-extrabold tracking-[-0.03em] text-xl">Details</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <Label>Client</Label>
                                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="(no client)" /></SelectTrigger>
                                    <SelectContent>
                                        {clients.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Address</Label>
                                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Description</Label>
                                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div>
                                <Label>Quoted amount (AUD)</Label>
                                <Input type="number" step="0.01" className="num" value={form.quoted_amount} onChange={(e) => setForm({ ...form, quoted_amount: e.target.value })} />
                            </div>
                            <div>
                                <Label>Reason for change (audit)</Label>
                                <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={save}>Save changes</Button>
                        </div>
                    </section>
                </TabsContent>

                <TabsContent value="variations" className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted">Total <span className="num text-ink">${totalVariations.toLocaleString()}</span></div>
                        <Button data-testid={JOBS.addVariationBtn} size="sm" variant="outline" onClick={() => setVOpen(true)} className="gap-2">
                            <Plus size={14} /> Add variation
                        </Button>
                    </div>
                    <div className="km-card">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-[color:var(--km-border)] text-[11px] uppercase tracking-[0.12em] text-muted">
                                    <th className="px-4 py-3 font-medium">Description</th>
                                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                                    <th className="px-4 py-3 font-medium">Approved</th>
                                </tr>
                            </thead>
                            <tbody>
                                {job.variations.map((v) => (
                                    <tr key={v.id} className="border-b border-[color:var(--km-border)] last:border-b-0">
                                        <td className="px-4 py-3">{v.description}</td>
                                        <td className="px-4 py-3 num text-right">${(v.amount || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3">
                                            {v.approved ? (
                                                <span className="text-xs text-forest">Approved</span>
                                            ) : (
                                                <span className="text-xs text-muted">Pending</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!job.variations.length && (
                                    <tr><td colSpan={3} className="py-8 text-center text-muted text-sm">No variations yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="costs" className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted">Total <span className="num text-ink">${totalCosts.toLocaleString()}</span></div>
                        <Button data-testid={JOBS.addContractorCostBtn} size="sm" variant="outline" onClick={() => setCOpen(true)} className="gap-2">
                            <Plus size={14} /> Add cost
                        </Button>
                    </div>
                    <div className="km-card">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-[color:var(--km-border)] text-[11px] uppercase tracking-[0.12em] text-muted">
                                    <th className="px-4 py-3 font-medium">Label</th>
                                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                                    <th className="px-4 py-3 font-medium">Added</th>
                                </tr>
                            </thead>
                            <tbody>
                                {job.contractor_costs.map((c) => (
                                    <tr key={c.id} className="border-b border-[color:var(--km-border)] last:border-b-0">
                                        <td className="px-4 py-3">{c.label}</td>
                                        <td className="px-4 py-3 num text-right">${(c.amount || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-xs text-muted num">{c.created_at?.slice(0, 10)}</td>
                                    </tr>
                                ))}
                                {!job.contractor_costs.length && (
                                    <tr><td colSpan={3} className="py-8 text-center text-muted text-sm">No costs yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="assignments" className="mt-6">
                    <div className="km-card p-8 text-center text-muted">
                        Assignments write UI ships in <strong className="text-ink">Phase 4</strong>.
                    </div>
                </TabsContent>
            </Tabs>

            {/* Add variation dialog */}
            <Dialog open={vOpen} onOpenChange={setVOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-extrabold tracking-[-0.03em] text-2xl">New variation</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Description</Label><Input value={vForm.description} onChange={(e) => setVForm({ ...vForm, description: e.target.value })} /></div>
                        <div><Label>Amount (AUD)</Label><Input type="number" step="0.01" className="num" value={vForm.amount} onChange={(e) => setVForm({ ...vForm, amount: e.target.value })} /></div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={vForm.approved} onChange={(e) => setVForm({ ...vForm, approved: e.target.checked })} />
                            Approved
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVOpen(false)}>Cancel</Button>
                        <Button onClick={addVariation} disabled={!vForm.description}>Add</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add contractor cost dialog */}
            <Dialog open={cOpen} onOpenChange={setCOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-extrabold tracking-[-0.03em] text-2xl">New contractor cost</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Label</Label><Input value={cForm.label} onChange={(e) => setCForm({ ...cForm, label: e.target.value })} /></div>
                        <div><Label>Amount (AUD)</Label><Input type="number" step="0.01" className="num" value={cForm.amount} onChange={(e) => setCForm({ ...cForm, amount: e.target.value })} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCOpen(false)}>Cancel</Button>
                        <Button onClick={addCost} disabled={!cForm.label}>Add</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
